import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { geoAlbersUsa, geoPath } from "d3-geo";
import { feature, mesh } from "topojson-client";
import statesTopo from "us-atlas/states-albers-10m.json";
import { Plus, Pencil, Trash2, Check, X, MapPin } from "lucide-react";
import { api } from "@/lib/api";

const projection = geoAlbersUsa().scale(1300).translate([487.5, 305]);
const path = geoPath();
const nation = feature(statesTopo, statesTopo.objects.nation);
const borders = mesh(statesTopo, statesTopo.objects.states, (a, b) => a !== b);
const inputCls = "bg-[#141414] border border-[#2a2a2a] px-3 py-2 text-[13px] text-[#e8e8e8] rounded focus:outline-none focus:border-[#4deb7a] transition-colors";

function CityChip({ c, isAdmin, active, onHover, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(c.name);
  const save = async () => {
    try { await api.put(`/admin/run-club/cities/${c.id}`, { name }); toast.success("City updated"); setEditing(false); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to update city"); }
  };
  const del = async () => {
    if (!window.confirm(`Remove ${c.name}?`)) return;
    try { await api.delete(`/admin/run-club/cities/${c.id}`); toast.success("City removed"); onSaved(); }
    catch { toast.error("Failed to remove city"); }
  };
  if (editing) {
    return (
      <div className="flex items-center gap-1.5" data-testid={`city-edit-${c.id}`}>
        <input autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()} className={`${inputCls} py-1 text-[12px] w-40`} data-testid="city-edit-input" />
        <button onClick={save} className="text-[#4deb7a]" data-testid="city-edit-save"><Check className="h-3.5 w-3.5" /></button>
        <button onClick={() => { setEditing(false); setName(c.name); }} className="text-[#8f8f8f]"><X className="h-3.5 w-3.5" /></button>
      </div>
    );
  }
  return (
    <div onMouseEnter={() => onHover(c.id)} onMouseLeave={() => onHover(null)} data-testid={`city-chip-${c.id}`}
      className={`group inline-flex items-center gap-1.5 border px-2.5 py-1 rounded-sm text-[11px] transition-colors ${active ? "border-[rgba(77,235,122,0.5)] text-[#4deb7a] bg-[rgba(77,235,122,0.06)]" : "border-[#222] text-[#bdbdbd]"}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-[#4deb7a]" />
      {c.name}
      {isAdmin && (
        <span className="flex items-center gap-1 ml-0.5 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
          <button onClick={() => setEditing(true)} className="text-[#8f8f8f] hover:text-[#e8e8e8]" data-testid={`city-edit-btn-${c.id}`}><Pencil className="h-3 w-3" /></button>
          <button onClick={del} className="text-[#8f8f8f] hover:text-[#c83232]" data-testid={`city-delete-btn-${c.id}`}><Trash2 className="h-3 w-3" /></button>
        </span>
      )}
    </div>
  );
}

export default function RunClubMap({ isAdmin }) {
  const [cities, setCities] = useState([]);
  const [hover, setHover] = useState(null);
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);

  const load = () => api.get("/run-club/cities").then((r) => setCities(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const points = useMemo(() => cities.map((c) => ({ ...c, xy: projection([c.lng, c.lat]) })).filter((c) => c.xy), [cities]);

  const add = async () => {
    if (!newName.trim()) return;
    setAdding(true);
    try { await api.post("/admin/run-club/cities", { name: newName }); toast.success("City added"); setNewName(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to add city"); }
    finally { setAdding(false); }
  };

  return (
    <section className="mt-10 mb-12 max-w-[920px]" data-testid="run-club-map">
      <div className="flex items-end justify-between gap-4 mb-4">
        <div>
          <div className="font-mono-dm uppercase tracking-[0.14em] text-[9px] text-[#4deb7a] mb-1.5">SATURDAYS · Run Club Launch</div>
          <h3 className="font-serif-pf text-[22px] text-[#f0f0f0] leading-tight">
            <span data-testid="run-club-city-count">{cities.length}</span> cities · Spring 2028
          </h3>
        </div>
      </div>

      <div className="relative border border-[#222] rounded-md bg-[#0a0a0a] overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 60% 40%, rgba(77,235,122,0.05), transparent 65%)" }} />
        <svg viewBox="0 0 975 610" className="relative w-full h-auto block" role="img" aria-label="Map of SATURDAYS run club cities">
          <defs>
            <radialGradient id="cityGlow">
              <stop offset="0%" stopColor="#4deb7a" stopOpacity="0.55" />
              <stop offset="100%" stopColor="#4deb7a" stopOpacity="0" />
            </radialGradient>
          </defs>
          <path d={path(nation)} className="map-nation" fill="#111" stroke="#2a2a2a" strokeWidth="1" />
          <path d={path(borders)} className="map-borders" fill="none" stroke="#1e1e1e" strokeWidth="0.7" />
          {points.map((c, i) => {
            const [x, y] = c.xy;
            const on = hover === c.id;
            return (
              <g key={c.id} onMouseEnter={() => setHover(c.id)} onMouseLeave={() => setHover(null)} className="cursor-default" data-testid={`city-dot-${c.id}`}>
                <circle cx={x} cy={y} r={on ? 26 : 16} fill="url(#cityGlow)" style={{ transition: "r 200ms ease" }}>
                  <animate attributeName="opacity" values="0.5;1;0.5" dur="3s" begin={`${(i % 6) * 0.5}s`} repeatCount="indefinite" />
                </circle>
                <circle cx={x} cy={y} r={on ? 5 : 3.5} className="map-dot" fill="#4deb7a" stroke="#0a0a0a" strokeWidth="1" />
                {on && (
                  <g pointerEvents="none">
                    <rect x={x + 10} y={y - 24} rx="3" width={c.name.length * 7.4 + 18} height="22" className="map-tip" fill="#0e0e0e" stroke="rgba(77,235,122,0.4)" />
                    <text x={x + 19} y={y - 9} className="map-tip-text" fill="#f0f0f0" fontSize="12" fontFamily="DM Mono, monospace">{c.name}</text>
                  </g>
                )}
              </g>
            );
          })}
        </svg>
        {cities.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center text-[12px] text-[#8f8f8f]">
            <MapPin className="h-4 w-4 mr-2" strokeWidth={1.5} /> Launch cities will appear here.
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5 mt-4" data-testid="run-club-city-list">
        {cities.map((c) => <CityChip key={c.id} c={c} isAdmin={isAdmin} active={hover === c.id} onHover={setHover} onSaved={load} />)}
      </div>

      {isAdmin && (
        <div className="flex items-center gap-2 mt-4">
          <input value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Add a city, e.g. Portland, OR" className={`${inputCls} w-64`} data-testid="city-add-input" />
          <button onClick={add} disabled={adding} className="inline-flex items-center gap-1.5 bg-[#4deb7a] hover:bg-[#3dd869] disabled:opacity-60 text-[#080808] text-[12px] font-medium px-4 py-2 rounded transition-colors" data-testid="city-add-btn">
            <Plus className="h-3.5 w-3.5" strokeWidth={1.5} /> {adding ? "Finding…" : "Add city"}
          </button>
        </div>
      )}
    </section>
  );
}
